import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { Input } from "@tellescope/ui";
import { FilterChip } from "../molecules/FilterChip";
import { ProductCard } from "../molecules/ProductCard";
import type { ShopProduct } from "../types";

interface ProductCatalogProps {
  products: ShopProduct[];
  title?: string;
  subtitle?: string;
  emptyMessage?: string;
  onLearnMore: (product: ShopProduct) => void;
  /** The org's configured primary theme color (hex), if any — threaded down to each ProductCard. */
  primaryColorHex?: string;
}

export const ProductCatalog = ({
  products,
  title,
  subtitle,
  emptyMessage,
  onLearnMore,
  primaryColorHex,
}: ProductCatalogProps) => {
  const { t } = useTranslation("shop");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [query, setQuery] = useState("");

  const categories = useMemo(() => {
    const found = new Set<string>();
    products.forEach((p) => p.category && found.add(p.category));
    return ["All", ...Array.from(found)];
  }, [products]);

  const recommended = useMemo(() => products.filter((p) => p.recommended), [products]);

  const matchesQuery = useCallback(
    (p: ShopProduct) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        (p.category?.toLowerCase().includes(q) ?? false) ||
        (p.description?.toLowerCase().includes(q) ?? false)
      );
    },
    [query]
  );

  const filtered = useMemo(
    () => products.filter((p) => (selectedCategory === "All" || p.category === selectedCategory) && matchesQuery(p)),
    [products, selectedCategory, matchesQuery]
  );
  const filteredRecommended = useMemo(
    () => recommended.filter((p) => (selectedCategory === "All" || p.category === selectedCategory) && matchesQuery(p)),
    [recommended, selectedCategory, matchesQuery]
  );

  if (products.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm text-muted-foreground">{emptyMessage || t("No products available.")}</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {title && <h2 className="text-2xl font-bold text-foreground mb-2">{title}</h2>}
      {subtitle && <p className="text-sm text-muted-foreground mb-7">{subtitle}</p>}

      <div className="relative mb-4 max-w-md">
        <Search
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("Search products…")}
          aria-label={t("Search products")}
          className="pl-11 text-base sm:text-sm"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-10">
        {categories.map((cat) => (
          <FilterChip
            key={cat}
            label={cat}
            selected={selectedCategory === cat}
            onClick={() => setSelectedCategory(cat)}
          />
        ))}
      </div>

      {filteredRecommended.length > 0 && (
        <section className="mb-2 -ml-4 sm:-ml-6 lg:-ml-8">
          <div className="flex overflow-x-auto pb-4 pr-4 snap-x snap-mandatory">
            {filteredRecommended.map((product) => (
              <div key={product.stripeProductId} className="flex-shrink-0 w-[300px] sm:w-[360px] snap-start">
                <ProductCard
                  product={product}
                  onLearnMore={onLearnMore}
                  secondaryBadge={product.badge || t("Recommended for you")}
                  descriptionLines={2}
                  className="rounded-none h-full"
                  primaryColorHex={primaryColorHex}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((product) => (
            <ProductCard
              key={product.stripeProductId}
              product={product}
              onLearnMore={onLearnMore}
              primaryColorHex={primaryColorHex}
            />
          ))}
        </div>
      ) : (
        <div className="py-16 text-center">
          <p className="text-sm text-muted-foreground">{t('No products match "{{query}}".', { query })}</p>
        </div>
      )}
    </div>
  );
};
